import { useEffect, useState, useRef, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Settings as SettingsIcon, Volume2, Download, Sunrise, Send, Loader2, Brain, ThumbsUp, ThumbsDown, RotateCcw, Moon, Lock, Play, Check, RefreshCw, ExternalLink, Sparkles } from "lucide-react";
import { SenderDomain } from "../components/SenderDomain";
import { ModelSwitch } from "../components/ModelSwitch";
import { ProjectConnections } from "../components/projects/ProjectConnections";
import { HandoffDigest } from "../components/projects/HandoffDigest";
import { NishuSettings } from "../components/NishuSettings";
import { toast } from "sonner";

function NudgeSettings() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(null);
  const [log, setLog] = useState([]);
  const [preview, setPreview] = useState(null);

  const load = async () => {
    try {
      const [a, b] = await Promise.all([api.get("/nudge/settings"), api.get("/nudge/log")]);
      setS(a.data); setLog(b.data);
    } catch (err) { console.error("[ARIA]", err); }
  };
  useEffect(() => { load(); }, []);

  const save = async (patch) => {
    setSaving(true);
    try { const { data } = await api.put("/nudge/settings", patch); setS(data); toast.success("Nudge settings saved"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };

  const sendNow = async (kind = "morning") => {
    setSending(kind);
    try { const { data } = await api.post(`/nudge/send-now?kind=${kind}`); toast.success(`${kind.startsWith("evening") ? "Wind-down" : "Nudge"} sent to ${data.to}${data.weekly_pdf ? " (weekly PDF attached)" : ""}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Send failed", { duration: 6000 }); }
    finally { setSending(null); }
  };

  const showPreview = async (kind = "morning") => {
    try { const { data } = await api.get(`/nudge/preview?kind=${kind}`); setPreview(data); } catch { toast.error("Preview failed"); }
  };

  if (!s) return null;
  const localHour = (h) => { const d = new Date(); d.setUTCHours(h, 0, 0, 0); return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }); };

  return (
    <div className="aria-card p-5 mb-6" data-testid="nudge-settings">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2"><Sunrise size={15} className="text-[var(--aria-primary)]" /><span className="text-xs uppercase tracking-[0.2em] text-[var(--aria-text-muted)] font-semibold">Morning nudge</span></div>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={!!s.enabled} onChange={e => save({ enabled: e.target.checked })} data-testid="nudge-enabled-toggle" />
          {s.enabled ? "On" : "Off"}
        </label>
      </div>
      <p className="text-sm text-[var(--aria-text-muted)] mb-4">Each morning ARIA emails your countdown tasks, due re-drills and what's coming up — so you never open the app cold.</p>
      <div className="grid sm:grid-cols-2 gap-3 mb-3">
        <div>
          <label className="text-xs text-[var(--aria-text-muted)]">Send to</label>
          <input value={s.email || ""} onChange={e => setS({ ...s, email: e.target.value })} onBlur={() => save({ email: s.email })} data-testid="nudge-email-input"
            className="w-full mt-1 px-3 py-2 rounded-md border border-[var(--aria-border)] bg-[var(--aria-bg)] outline-none focus:ring-2 focus:ring-[var(--aria-primary)] text-sm" />
        </div>
        <div>
          <label className="text-xs text-[var(--aria-text-muted)]">Time (UTC {String(s.hour_utc).padStart(2, "0")}:00 · {localHour(s.hour_utc)} your time)</label>
          <select value={s.hour_utc} onChange={e => save({ hour_utc: parseInt(e.target.value) })} data-testid="nudge-hour-select"
            className="w-full mt-1 px-3 py-2 rounded-md border border-[var(--aria-border)] bg-[var(--aria-bg)] outline-none text-sm">
            {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{String(h).padStart(2, "0")}:00 UTC — {localHour(h)}</option>)}
          </select>
        </div>
      </div>
      <div className="flex items-center gap-3 flex-wrap">
        <button onClick={() => sendNow("morning")} disabled={!!sending} data-testid="nudge-send-now-button" className="btn btn-primary px-4 py-2 text-sm inline-flex items-center gap-2 disabled:opacity-60">
          {sending === "morning" ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Send me today's nudge now
        </button>
        <button onClick={() => showPreview("morning")} data-testid="nudge-preview-button" className="text-sm text-[var(--aria-text-muted)] hover:text-[var(--aria-text)]">Preview</button>
        {saving && <Loader2 size={12} className="animate-spin text-[var(--aria-text-muted)]" />}
      </div>

      <div className="mt-5 pt-4 border-t border-[var(--aria-border)]" data-testid="evening-settings">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2"><Moon size={14} className="text-[var(--aria-accent)]" /><span className="text-xs uppercase tracking-[0.2em] text-[var(--aria-text-muted)] font-semibold">Nightly wind-down</span></div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={!!s.evening_enabled} onChange={e => save({ evening_enabled: e.target.checked })} data-testid="evening-enabled-toggle" />
            {s.evening_enabled ? "On" : "Off"}
          </label>
        </div>
        <p className="text-sm text-[var(--aria-text-muted)] mb-3">A short evening email: what you drilled today, what carried over, and what tomorrow holds.</p>
        <label className="flex items-center gap-2 text-sm cursor-pointer mb-3" data-testid="weekly-pdf-row">
          <input type="checkbox" checked={s.weekly_pdf_enabled !== false} onChange={e => save({ weekly_pdf_enabled: e.target.checked })} data-testid="weekly-pdf-toggle" />
          Attach the Weekly Intelligence Report PDF to Sunday's wind-down
        </label>
        <div className="grid sm:grid-cols-2 gap-3 items-end">
          <div>
            <label className="text-xs text-[var(--aria-text-muted)]">Time (UTC {String(s.evening_hour_utc).padStart(2, "0")}:00 · {localHour(s.evening_hour_utc)} your time)</label>
            <select value={s.evening_hour_utc} onChange={e => save({ evening_hour_utc: parseInt(e.target.value) })} data-testid="evening-hour-select"
              className="w-full mt-1 px-3 py-2 rounded-md border border-[var(--aria-border)] bg-[var(--aria-bg)] outline-none text-sm">
              {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{String(h).padStart(2, "0")}:00 UTC — {localHour(h)}</option>)}
            </select>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => sendNow("evening")} disabled={!!sending} data-testid="evening-send-now-button" className="btn px-4 py-2 text-sm border border-[var(--aria-border)] hover:border-[var(--aria-primary)] inline-flex items-center gap-2 disabled:opacity-60">
              {sending === "evening" ? <Loader2 size={14} className="animate-spin" /> : <Moon size={14} />} Send tonight's now
            </button>
            <button onClick={() => showPreview("evening")} data-testid="evening-preview-button" className="text-sm text-[var(--aria-text-muted)] hover:text-[var(--aria-text)]">Preview</button>
            <button onClick={() => sendNow("evening&with_weekly=true")} disabled={!!sending} data-testid="weekly-pdf-send-now" className="text-sm text-[var(--aria-primary)] hover:underline disabled:opacity-50">Send with weekly PDF now</button>
          </div>
        </div>
      </div>

      {log.length > 0 && (
        <div className="mt-4 text-xs text-[var(--aria-text-muted)]" data-testid="nudge-log">Last sent: {new Date(log[0].sent_at).toLocaleString()} → {log[0].to} ({log[0].kind || "morning"})</div>
      )}
      {preview && (
        <div className="mt-4 border border-[var(--aria-border)] rounded-md overflow-hidden" data-testid="nudge-preview">
          <iframe title="nudge-preview" srcDoc={preview.html} className="w-full h-[420px] bg-[#030711] border-0" />
        </div>
      )}
    </div>
  );
}

function LearningLoop() {
  const [data, setData] = useState(null);
  const load = async () => { try { const { data } = await api.get("/knowledge/learning"); setData(data); } catch (err) { console.error("[ARIA]", err); } };
  useEffect(() => { load(); }, []);
  const reset = async () => { await api.post("/knowledge/learning/reset"); toast.success("Learned rankings reset"); load(); };
  if (!data) return null;
  return (
    <div className="aria-card p-5 mb-6" data-testid="learning-loop">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2"><Brain size={15} className="text-[var(--aria-primary)]" /><span className="text-xs uppercase tracking-[0.2em] text-[var(--aria-text-muted)] font-semibold">Learning loop</span></div>
        <div className="flex items-center gap-3 text-xs text-[var(--aria-text-muted)]" data-testid="learning-totals">
          <span className="inline-flex items-center gap-1"><ThumbsUp size={11} /> {data.totals.up}</span>
          <span className="inline-flex items-center gap-1"><ThumbsDown size={11} /> {data.totals.down}</span>
        </div>
      </div>
      <p className="text-sm text-[var(--aria-text-muted)] mb-3">Your thumbs-up and thumbs-down in Ask ARIA retrain retrieval: sources behind down-voted answers rank lower next time, up-voted ones rank higher.</p>
      {data.sources.length === 0 ? (
        <div className="text-sm text-[var(--aria-text-muted)]" data-testid="learning-empty">Nothing learned yet — rate a few answers in Ask ARIA.</div>
      ) : (
        <div className="space-y-1.5" data-testid="learning-sources">
          {data.sources.map(r => (
            <div key={r.source_title} className="flex items-center justify-between text-sm px-3 py-2 rounded-md bg-[var(--aria-surface-2)]" data-testid={`learning-source-${r.source_title}`}>
              <span className="truncate">{r.source_title}</span>
              <span className={`font-mono text-xs ${r.weight < 1 ? "text-amber-400" : r.weight > 1 ? "text-emerald-400" : "text-[var(--aria-text-muted)]"}`}>
                ×{r.weight.toFixed(2)} · {r.ups}↑ {r.downs}↓
              </span>
            </div>
          ))}
        </div>
      )}
      {data.sources.length > 0 && (
        <button onClick={reset} data-testid="learning-reset-button" className="mt-3 text-xs text-[var(--aria-text-muted)] hover:text-[var(--aria-text)] inline-flex items-center gap-1"><RotateCcw size={11} /> Reset learned rankings</button>
      )}
    </div>
  );
}

function VoiceCard({ v, selected, onSelect, onPreview, previewing }) {
  const locked = v.locked;
  return (
    <div
      role="button"
      tabIndex={locked ? -1 : 0}
      onClick={() => !locked && onSelect(v.voice_id)}
      data-testid={`voice-card-${v.voice_id}`}
      className={`relative rounded-lg border p-3 text-left transition-colors ${
        locked
          ? "border-[var(--aria-border)] opacity-70 cursor-not-allowed bg-[var(--aria-bg)]"
          : selected
            ? "border-[var(--aria-primary)] bg-[var(--aria-primary)]/10 cursor-pointer"
            : "border-[var(--aria-border)] hover:border-[var(--aria-primary)]/60 cursor-pointer"
      }`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className="font-medium text-sm truncate">{v.name}</span>
          {selected && !locked && <Check size={13} className="text-[var(--aria-primary)] shrink-0" />}
        </div>
        {locked ? (
          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-amber-400/90 shrink-0"><Lock size={11} /> Premium</span>
        ) : (
          <button
            onClick={(e) => { e.stopPropagation(); onPreview(v); }}
            data-testid={`voice-preview-${v.voice_id}`}
            title={`Preview ${v.name}`}
            className="shrink-0 w-7 h-7 rounded-full flex items-center justify-center border border-[var(--aria-border)] hover:border-[var(--aria-primary)] hover:text-[var(--aria-primary)]"
          >
            {previewing ? <Loader2 size={12} className="animate-spin" /> : <Play size={12} />}
          </button>
        )}
      </div>
      <div className="text-[11px] text-[var(--aria-text-muted)] mt-1 leading-snug">{v.description}</div>
    </div>
  );
}

function VoiceGallery({ org }) {
  const LEGACY = ["nova", "alloy", "echo", "shimmer", "onyx", "fable"];
  const [cat, setCat] = useState({ voices: [], default: "", premium_unlocked: false });
  const [voice, setVoice] = useState("");
  const [speed, setSpeed] = useState(Math.min(1.2, org?.tts_speed || 1.1));
  const [busy, setBusy] = useState(false);
  const [rechecking, setRechecking] = useState(false);
  const [previewId, setPreviewId] = useState(null);
  const audioRef = useRef(null);

  const load = useCallback(async (refresh = false) => {
    try {
      const { data } = await api.get(`/voice/voices${refresh ? "?refresh=true" : ""}`);
      setCat(data);
      setVoice((cur) => {
        const stored = LEGACY.includes(org?.tts_voice) ? "" : org?.tts_voice;
        const pick = cur || stored || data.default;
        const match = (data.voices || []).find((x) => x.voice_id === pick);
        return match && !match.locked ? pick : data.default;
      });
    } catch { /* ignore */ }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [org]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => () => { if (audioRef.current) audioRef.current.pause(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/settings/aria", { tts_voice: voice, tts_speed: speed });
      toast.success("Voice preferences saved");
    } catch { toast.error("Only admins can update ARIA voice settings"); }
    finally { setBusy(false); }
  };

  const recheck = async () => {
    setRechecking(true);
    await load(true);
    setRechecking(false);
  };

  const previewVoice = async (v) => {
    if (v.locked) return;
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    setPreviewId(v.voice_id);
    try {
      const { data } = await api.post("/tts", {
        text: `Hello, I'm ARIA, your academic intelligence system. This is the ${v.name} voice.`,
        voice: v.voice_id, speed,
      }, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const audio = new Audio(url); audioRef.current = audio;
      audio.onended = () => { URL.revokeObjectURL(url); setPreviewId(null); };
      audio.onerror = () => { URL.revokeObjectURL(url); setPreviewId(null); };
      await audio.play();
    } catch { toast.error("Voice preview failed"); setPreviewId(null); }
  };

  const free = cat.voices.filter((v) => v.category === "free");
  const premium = cat.voices.filter((v) => v.category === "premium");

  return (
    <div className="aria-card p-5 mb-6" data-testid="voice-gallery">
      <div className="flex items-center gap-2 mb-1"><Volume2 size={15} className="text-[var(--aria-primary)]" /><span className="text-xs uppercase tracking-[0.2em] text-[var(--aria-text-muted)] font-semibold">ARIA's voice (ElevenLabs)</span></div>
      <p className="text-xs text-[var(--aria-text-muted)] mb-4">Pick the voice ARIA speaks in across chat, interviews, briefings and read-aloud. Tap the play icon to preview.</p>

      <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--aria-text-muted)] font-semibold mb-2">Included voices</div>
      <div className="grid sm:grid-cols-2 gap-2.5 mb-6">
        {free.map((v) => (
          <VoiceCard key={v.voice_id} v={v} selected={voice === v.voice_id}
            onSelect={setVoice} onPreview={previewVoice} previewing={previewId === v.voice_id} />
        ))}
      </div>

      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--aria-text-muted)] font-semibold flex items-center gap-1.5">
          <Sparkles size={12} className="text-amber-400" /> Premium library voices
        </div>
        {cat.premium_unlocked
          ? <span className="text-[10px] text-emerald-400 uppercase tracking-wider">Unlocked</span>
          : <span className="text-[10px] text-amber-400/90 uppercase tracking-wider inline-flex items-center gap-1"><Lock size={10} /> Locked</span>}
      </div>
      {!cat.premium_unlocked && (
        <div className="rounded-md border border-amber-400/25 bg-amber-400/5 px-3 py-2.5 mb-3 text-xs text-[var(--aria-text-muted)]" data-testid="premium-locked-notice">
          These studio-grade voices need a paid ElevenLabs plan. Upgrade your key's plan and they'll unlock here automatically.
          <div className="mt-2 flex items-center gap-4">
            <a href="https://elevenlabs.io/app/subscription" target="_blank" rel="noreferrer"
              data-testid="voice-upgrade-link" className="text-[var(--aria-primary)] hover:underline inline-flex items-center gap-1">
              <ExternalLink size={11} /> Upgrade ElevenLabs
            </a>
            <button onClick={recheck} disabled={rechecking} data-testid="voice-recheck-button"
              className="text-[var(--aria-text-muted)] hover:text-[var(--aria-text)] inline-flex items-center gap-1 disabled:opacity-50">
              <RefreshCw size={11} className={rechecking ? "animate-spin" : ""} /> Re-check
            </button>
          </div>
        </div>
      )}
      <div className="grid sm:grid-cols-2 gap-2.5 mb-6">
        {premium.map((v) => (
          <VoiceCard key={v.voice_id} v={v} selected={voice === v.voice_id}
            onSelect={setVoice} onPreview={previewVoice} previewing={previewId === v.voice_id} />
        ))}
      </div>

      <label className="text-xs text-[var(--aria-text-muted)]">Speech speed: {speed.toFixed(2)}x</label>
      <input type="range" min="0.7" max="1.2" step="0.02" value={speed} onChange={(e) => setSpeed(parseFloat(e.target.value))}
        data-testid="settings-speed-slider" className="w-full mt-1 mb-4" />
      <button onClick={save} disabled={busy || !voice} data-testid="settings-save-button"
        className="btn btn-primary px-4 py-2 text-sm disabled:opacity-50">Save voice</button>
    </div>
  );
}

export default function PersonalSettings() {
  const { user, org } = useAuth();

  const exportData = async () => {
    try {
      const [notes, videos, turbo, interviews] = await Promise.all([
        api.get("/notes"), api.get("/youtube"), api.get("/turbolearn"), api.get("/interviews"),
      ]);
      const blob = new Blob([JSON.stringify({ notes: notes.data, videos: videos.data, turbolearn: turbo.data, interviews: interviews.data }, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "aria-export.json"; a.click();
      URL.revokeObjectURL(url);
      toast.success("Export downloaded");
    } catch { toast.error("Export failed"); }
  };

  return (
    <div className="h-screen overflow-y-auto p-8">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-2 mb-6"><SettingsIcon size={22} className="text-[var(--aria-primary)]" /><h1 className="font-display text-3xl">Settings</h1></div>

        <div className="aria-card p-5 mb-6">
          <div className="text-xs uppercase tracking-[0.2em] text-[var(--aria-text-muted)] font-semibold mb-4">Profile</div>
          <div className="text-sm"><span className="text-[var(--aria-text-muted)]">Name:</span> {user?.name}</div>
          <div className="text-sm mt-1"><span className="text-[var(--aria-text-muted)]">Email:</span> {user?.email}</div>
        </div>

        <VoiceGallery org={org} />

        <ModelSwitch />
        <ProjectConnections />
        <HandoffDigest />
        <NishuSettings />
        <NudgeSettings />
        <SenderDomain />
        <LearningLoop />

        <div className="aria-card p-5">
          <div className="text-xs uppercase tracking-[0.2em] text-[var(--aria-text-muted)] font-semibold mb-4">Data & privacy</div>
          <p className="text-sm text-[var(--aria-text-muted)] mb-4">Export everything ARIA has connected for you, or delete a source from its own page (Notes, Learn, TurboLearn, Research).</p>
          <button onClick={exportData} data-testid="export-data-button"
            className="btn px-4 py-2 rounded-md border border-[var(--aria-border)] hover:border-[var(--aria-primary)] text-sm flex items-center gap-2">
            <Download size={14} /> Export my data
          </button>
        </div>
      </div>
    </div>
  );
}
